import Phaser from "phaser";
import { findEntity, type PlayerIndex, type ShipColor, type WorldState } from "../../sim";
import type { ImageKey } from "../assets";
import type { Interpolator, Pose } from "./interpolation";
import { DIVIDER_PX, minimapLayout, type ViewportLayout } from "./layout";
import { drawObjective } from "./objective-art";
import { FUEL_STATION_COLOR, POWER_UP_ART } from "./power-up-art";

/** legacy WorldMapSpriteManager: map size 100 units, ship icons r = 10. */
const MINIMAP_UNITS = 100;
const MINIMAP_SHIP_ICON_UNITS = 20;
/** Power-up pods on the minimap: dot radius in minimap units. */
const MINIMAP_POD_UNITS = 3;

interface Minimap {
	container: Phaser.GameObjects.Container;
	ships: Map<PlayerIndex, Phaser.GameObjects.Image>;
	/** Redrawn every frame: power-up pods. */
	pods: Phaser.GameObjects.Graphics;
	size: number;
}

/**
 * Screen-space overlay rendered by its own unzoomed camera: the split-screen divider and a
 * circular minimap per viewport showing planets and every player's ship.
 */
export class HudView {
	readonly layer: Phaser.GameObjects.Layer;
	private readonly divider: Phaser.GameObjects.Rectangle;
	/** The horizontal line of a 2×2 split (3–4 viewers). */
	private readonly dividerH: Phaser.GameObjects.Rectangle;
	private minimaps: Minimap[] = [];
	private readonly pose: Pose = { x: 0, y: 0, angle: 0 };

	constructor(
		private readonly scene: Phaser.Scene,
		readonly camera: Phaser.Cameras.Scene2D.Camera,
		private readonly world: WorldState,
	) {
		this.layer = scene.add.layer();
		this.divider = new Phaser.GameObjects.Rectangle(scene, 0, 0, 1, 1, 0xffffff).setOrigin(0);
		this.layer.add(this.divider);
		this.dividerH = new Phaser.GameObjects.Rectangle(scene, 0, 0, 1, 1, 0xffffff).setOrigin(0);
		this.layer.add(this.dividerH);
	}

	setLayout(width: number, height: number, viewports: ViewportLayout[]): void {
		this.camera.setViewport(0, 0, width, height);
		this.divider
			.setVisible(viewports.length > 1)
			.setPosition(Math.floor(width / 2) - DIVIDER_PX, 0)
			.setSize(DIVIDER_PX, height);
		this.dividerH
			.setVisible(viewports.length > 2)
			.setPosition(0, Math.floor(height / 2) - DIVIDER_PX)
			.setSize(width, DIVIDER_PX);

		for (const m of this.minimaps) m.container.destroy();
		this.minimaps = viewports.map((vp) => this.createMinimap(vp));
	}

	render(interp: Interpolator, alpha: number): void {
		const { width, height } = this.world.size;
		for (const map of this.minimaps) {
			this.renderPods(map);
			for (const [player, icon] of map.ships) {
				const shipId = this.world.players[player]?.shipId;
				const ship = shipId == null ? undefined : findEntity(this.world, shipId);
				icon.setVisible(ship?.kind === "ship");
				if (ship?.kind !== "ship") continue;
				const pose = interp.sample(ship, alpha, this.world.size, this.pose);
				const x = minimapCoord(pose.x, width, map.size, this.world);
				const y = minimapCoord(pose.y, height, map.size, this.world);
				icon.setPosition(x, y).setRotation(pose.angle);
			}
		}
	}

	private renderPods(map: Minimap): void {
		const { width, height } = this.world.size;
		const r = Math.max(1.5, (MINIMAP_POD_UNITS / MINIMAP_UNITS) * map.size);
		map.pods.clear();
		drawObjective(
			map.pods,
			this.world,
			(x, y) => ({
				x: minimapCoord(x, width, map.size, this.world),
				y: minimapCoord(y, height, map.size, this.world),
			}),
			minimapScale(map.size, this.world),
			(player) => {
				const icon = map.ships.get(player);
				return icon?.visible ? { x: icon.x, y: icon.y } : null;
			},
			true,
		);
		for (const e of this.world.entities) {
			if (e.kind !== "pickup") continue;
			const x = minimapCoord(e.pos.x, width, map.size, this.world);
			const y = minimapCoord(e.pos.y, height, map.size, this.world);
			map.pods.fillStyle(0x000000, 0.6).fillCircle(x, y, r + 1);
			map.pods.fillStyle(POWER_UP_ART[e.item].color, 1).fillCircle(x, y, r);
		}
		// Fueling stations (I-026): squares, so they don't read as pods.
		for (const s of this.world.fuelStations ?? []) {
			const x = minimapCoord(s.pos.x, width, map.size, this.world);
			const y = minimapCoord(s.pos.y, height, map.size, this.world);
			map.pods.fillStyle(0x000000, 0.6).fillRect(x - r - 1, y - r - 1, 2 * r + 2, 2 * r + 2);
			map.pods.fillStyle(FUEL_STATION_COLOR, 1).fillRect(x - r, y - r, 2 * r, 2 * r);
		}
	}

	private createMinimap(vp: ViewportLayout): Minimap {
		const layout = minimapLayout(vp);
		const { size } = layout;
		const container = this.scene.make.container({ x: layout.cx, y: layout.cy }, false);
		container.add(
			new Phaser.GameObjects.Image(this.scene, 0, 0, "minimapBg").setDisplaySize(size, size),
		);

		const { width, height } = this.world.size;
		for (const e of this.world.entities) {
			if (e.kind !== "planet") continue;
			const planet = new Phaser.GameObjects.Image(this.scene, 0, 0, "minimapPlanet");
			planet
				.setPosition(
					minimapCoord(e.pos.x, width, size, this.world),
					minimapCoord(e.pos.y, height, size, this.world),
				)
				.setDisplaySize(
					e.radius * 2 * minimapScale(size, this.world),
					e.radius * 2 * minimapScale(size, this.world),
				);
			container.add(planet);
		}

		const pods = this.scene.make.graphics({}, false);
		container.add(pods);

		const ships = new Map<PlayerIndex, Phaser.GameObjects.Image>();
		const iconSize = (MINIMAP_SHIP_ICON_UNITS / MINIMAP_UNITS) * size;
		for (const player of this.world.players) {
			const key = MINIMAP_ICON[player.color];
			const icon = new Phaser.GameObjects.Image(this.scene, 0, 0, key).setDisplaySize(
				iconSize,
				iconSize,
			);
			container.add(icon);
			ships.set(player.index, icon);
		}

		this.layer.add(container);
		return { container, ships, pods, size };
	}
}

const MINIMAP_ICON: Record<ShipColor, ImageKey> = {
	blue: "minimapShipBlue",
	red: "minimapShipRed",
	green: "minimapShipGreen",
	yellow: "minimapShipYellow",
};

/** Minimap pixels per world unit: uniform, so non-square worlds keep their proportions. */
function minimapScale(size: number, world: WorldState): number {
	return size / Math.max(world.size.width, world.size.height);
}

/** Minimap offset from the map centre for a world coordinate on an axis of length `axis`. */
function minimapCoord(v: number, axis: number, size: number, world: WorldState): number {
	const wrapped = ((v % axis) + axis) % axis;
	return (wrapped - axis / 2) * minimapScale(size, world);
}
